import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Loader2, Home, Calendar, Users, MapPin, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GuestPortalPage = () => {
  const { portalToken } = useParams();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const loadPortal = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/messaging/guest/${portalToken}`);
      if (!resp.ok) throw new Error('Portal not found');
      const d = await resp.json();
      setData(d);
      setMessages(d.messages || []);
    } catch (err) {
      setError('This guest portal link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPortal(); }, [portalToken]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/messaging/guest/${portalToken}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage }),
      });
      if (resp.ok) {
        const msg = await resp.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#FF385C] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md text-center" data-testid="portal-error">
          <Home className="w-10 h-10 text-[#FF385C] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Portal Not Available</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const { booking, property } = data;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="guest-portal">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF385C]/10 flex items-center justify-center">
              <Home className="w-5 h-5 text-[#FF385C]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800" data-testid="portal-property-name">{booking.property_name}</h1>
              <p className="text-xs text-gray-500">Guest Portal for {booking.guest_name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Booking Details Card */}
        <div className="bg-white rounded-2xl border p-5" data-testid="portal-booking-details">
          <h2 className="text-sm font-bold text-gray-800 mb-3">Your Stay</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4 text-[#FF385C]" />
              <div>
                <p className="text-xs text-gray-400">Check-in</p>
                <p className="font-medium">{booking.check_in}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4 text-[#FF385C]" />
              <div>
                <p className="text-xs text-gray-400">Check-out</p>
                <p className="font-medium">{booking.check_out}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4 text-[#FF385C]" />
              <div>
                <p className="text-xs text-gray-400">Guests</p>
                <p className="font-medium">{booking.num_guests}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4 text-[#FF385C]" />
              <div>
                <p className="text-xs text-gray-400">Status</p>
                <p className="font-medium capitalize">{booking.status}</p>
              </div>
            </div>
          </div>
          {property?.address && (
            <div className="mt-3 pt-3 border-t flex items-start gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-[#FF385C] shrink-0 mt-0.5" />
              <span>{property.address}{property.city ? `, ${property.city}` : ''}</span>
            </div>
          )}
          {property?.check_in_instructions && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs font-semibold text-gray-700 mb-1">Check-in Instructions</p>
              <p className="text-xs text-gray-500 whitespace-pre-wrap">{property.check_in_instructions}</p>
            </div>
          )}
          {property?.house_rules && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs font-semibold text-gray-700 mb-1">House Rules</p>
              <p className="text-xs text-gray-500 whitespace-pre-wrap">{property.house_rules}</p>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="bg-white rounded-2xl border overflow-hidden" data-testid="portal-messages">
          <div className="px-5 py-3 border-b">
            <h2 className="text-sm font-bold text-gray-800">Messages with your host</h2>
          </div>
          <div className="h-72 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <p className="text-center text-gray-400 text-sm py-8">No messages yet. Send a message to your host below.</p>
            )}
            {messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`flex ${msg.sender_type === 'guest' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  msg.sender_type === 'guest'
                    ? 'bg-[#FF385C] text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className={`text-[10px] mt-1 ${
                    msg.sender_type === 'guest' ? 'text-white/70' : 'text-gray-400'
                  }`}>
                    {msg.sender_name} &middot; {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="px-4 py-3 border-t bg-gray-50">
            <div className="flex items-center gap-2">
              <Input
                data-testid="guest-message-input"
                placeholder="Type a message to your host..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                className="flex-1 rounded-xl h-10"
              />
              <Button
                data-testid="guest-send-btn"
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                className="bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-xl h-10 px-4"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestPortalPage;
